import { useCallback, useState, type RefObject } from "react";
import type { MapRef } from "react-map-gl/maplibre";
import type { Restroom } from "../types";
import { fetchRestroom } from "./api";
import { localePath, restroomIdFromPath, wcPath } from "./routes";
import { useLocale, useT } from "../i18n";
import { allowPublicRestroom } from "./analytics";

/**
 * Výběr WC a vše kolem URL: drží `/wc/:id` (ať jde detail sdílet), umí
 * deep-link po načtení mapy a sdílení (nativní share / kopie odkazu).
 *
 * Všechny adresy jdou přes `localePath`, takže návštěvník `/en/...` zůstane
 * v anglické větvi webu — jinak by mu výběr WC přepsal URL na českou.
 */
export function useRestroomSelection(mapRef: RefObject<MapRef | null>) {
  const { locale } = useLocale();
  const t = useT();
  const [selected, setSelected] = useState<Restroom | null>(null);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  // Výběr WC drží URL na /wc/:id, ať jde detail sdílet; zavření vrací na mapu.
  const select = useCallback(
    (restroom: Restroom) => {
      allowPublicRestroom(restroom.id);
      setSelected(restroom);
      setHighlightId(restroom.id);
      history.replaceState(null, "", localePath(locale, wcPath(restroom.id)));
    },
    [locale],
  );

  const close = useCallback(() => {
    setSelected(null);
    history.replaceState(null, "", localePath(locale, "/"));
  }, [locale]);

  // Sdílení detailu: nativní share (mobil), jinak kopie odkazu do schránky.
  const share = useCallback(
    async (restroom: Restroom) => {
      const url = `${window.location.origin}${localePath(locale, wcPath(restroom.id))}`;
      const title = `${restroom.name ?? t("detail.defaultName")} – Khakham`;
      if (navigator.share) {
        try {
          await navigator.share({ title, url });
        } catch {
          /* uživatel sdílení zrušil */
        }
        return;
      }
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        window.prompt(t("share.copyPrompt"), url);
      }
    },
    [locale, t],
  );

  // Initialize the deep link without waiting for style-dependent animation frames.
  const openDeepLink = useCallback(() => {
    const id = restroomIdFromPath(window.location.pathname);
    if (id == null) return;
    fetchRestroom(id)
      .then((restroom) => {
        mapRef.current?.jumpTo({ center: [restroom.lng, restroom.lat], zoom: 17 });
        setSelected(restroom);
        setHighlightId(restroom.id);
      })
      .catch(() => history.replaceState(null, "", localePath(locale, "/")));
  }, [mapRef, locale]);

  return { selected, highlightId, copied, select, close, share, openDeepLink };
}
